// src/pages/FaturaGirisi.js
import React, { useEffect, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import "pdfjs-dist/legacy/build/pdf.worker.entry";

const emptySatir = (tarih) => ({
  faturaNo: "",
  firmaAdi: "",
  firmaId: "",
  tarih,
  plaka: "",
  urunAdi: "",
  fiyat: "",
  adet: 1,
  iskonto: "",
  kdv: "",
  aciklama: "",
});

const normalizeTR = (value = "") =>
  value
    .toLocaleUpperCase("tr-TR")
    .replace(/[İI]/g, "I")
    .replace(/[Ş]/g, "S")
    .replace(/[Ğ]/g, "G")
    .replace(/[Ü]/g, "U")
    .replace(/[Ö]/g, "O")
    .replace(/[Ç]/g, "C")
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const stripCompanyWords = (value = "") =>
  normalizeTR(value)
    .split(" ")
    .filter((part) => ![
      "A", "S", "AS", "LTD", "STI", "SIRKETI", "SAN", "SANAYI", "TIC",
      "TICARET", "VE", "ANONIM", "LIMITED", "INS", "INSAAT",
    ].includes(part))
    .join(" ");

const parseTRNumber = (raw = "") => {
  const cleaned = String(raw)
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(\D|$))/g, "")
    .replace(",", ".");
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
};

const toInputDate = (raw = "") => {
  const match = String(raw).match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (!match) return "";
  const [, d, m, y] = match;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

const findFirst = (text, patterns) => {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return String(match[1]).trim();
  }
  return "";
};

const getPdfPageText = async (page) => {
  const content = await page.getTextContent();
  const rows = new Map();

  for (const item of content.items) {
    const text = String(item.str || "").trim();
    if (!text) continue;

    const [, , , , x = 0, y = 0] = item.transform || [];
    const key = Math.round(y / 3) * 3;
    if (!rows.has(key)) rows.set(key, []);
    rows.get(key).push({ x, text });
  }

  return [...rows.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([, parts]) =>
      parts
        .sort((a, b) => a.x - b.x)
        .map((part) => part.text)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean)
    .join("\n");
};

const extractTaxNumbers = (text) => {
  const candidates = new Set();
  const matches = text.matchAll(
    /(?:VKN|V\.?K\.?N\.?|VERGI\s*(?:KIMLIK)?\s*NO|VERGİ\s*(?:KİMLİK)?\s*NO|TCKN|T\.?C\.?\s*KIMLIK\s*NO|T\.?C\.?\s*KİMLİK\s*NO)\D{0,20}(\d[\d\s.-]{8,14}\d)/gi
  );

  for (const match of matches) {
    const digits = match[1].replace(/\D/g, "");
    if (digits.length === 10 || digits.length === 11) candidates.add(digits);
  }

  return candidates;
};

const scoreFirmName = (firmName, normalizedText) => {
  const normalizedName = stripCompanyWords(firmName);
  const tokens = normalizedName.split(" ").filter((part) => part.length > 2);
  if (!tokens.length) return 0;

  const hits = tokens.filter((token) => normalizedText.includes(token)).length;
  if (normalizedText.includes(normalizedName)) return 1;
  return hits / tokens.length;
};

const findFirm = (text, firmalar) => {
  const normalizedText = normalizeTR(text);
  const taxNumbers = extractTaxNumbers(text);

  const taxMatched = firmalar.find((firma) => {
    const firmaTax = String(firma.vergiNo || firma.vkn || firma.taxNo || "").replace(/\D/g, "");
    return firmaTax && taxNumbers.has(firmaTax);
  });
  if (taxMatched) return taxMatched;

  return [...firmalar]
    .map((firma) => ({ firma, score: scoreFirmName(firma.adi || "", normalizedText) }))
    .filter(({ score }) => score >= 0.65)
    .sort((a, b) => b.score - a.score || String(b.firma.adi || "").length - String(a.firma.adi || "").length)[0]?.firma;
};

const findPlate = (text, plakalar) => {
  const compactText = normalizeTR(text).replace(/\s/g, "");
  const knownPlate = plakalar.find((p) => {
    const plate = normalizeTR(p || "").replace(/\s/g, "");
    return plate && compactText.includes(plate);
  });
  if (knownPlate) return knownPlate;

  const match = normalizeTR(text).match(/\b(\d{2}\s?[A-Z]{1,3}\s?\d{2,4})\b/);
  return match?.[1]?.replace(/\s+/g, " ") || "";
};

const findDate = (text) => findFirst(text, [
  /(?:FATURA\s*TARİHİ|FATURA\s*TARIHI|DÜZENLEME\s*TARİHİ|DUZENLEME\s*TARIHI|BELGE\s*TARİHİ|BELGE\s*TARIHI|ISSUE\s*DATE)\D{0,35}(\d{1,2}[./-]\d{1,2}[./-]\d{4})/i,
  /(?:TARİH|TARIH)\D{0,20}(\d{1,2}[./-]\d{1,2}[./-]\d{4})/i,
  /(\d{1,2}[./-]\d{1,2}[./-]\d{4})/,
]);

const findKdv = (text) => {
  const percentRate = findFirst(text, [
    /% ?(1|8|10|18|20)(?:[,.]0+)?\b/,
    /\b(1|8|10|18|20)(?:[,.]0+)?\s*%/,
  ]);
  if (percentRate) return percentRate;

  const rateLabel = findFirst(text, [
    /(?:KDV\s*ORANI|ORAN)\D{0,20}(1|8|10|18|20)(?:[,.]0+)?\b/i,
  ]);
  if (rateLabel) return rateLabel;

  return "";
};

const getLastNumberFromLine = (line) => {
  const matches = [...String(line).matchAll(/(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d{2,4})?/g)];
  if (!matches.length) return null;
  return parseTRNumber(matches[matches.length - 1][0]);
};

const findAmount = (text, kdvRate) => {
  const lines = text.split(/\n+/).filter(Boolean);
  const normalizedLines = lines.map((line) => ({ raw: line, normalized: normalizeTR(line) }));
  const netLabels = ["MAL HIZMET TOPLAM", "ARA TOPLAM", "KDV MATRAH", "MATRAH", "VERGI HARIC", "VERGISIZ"];
  const grossLabels = ["ODENECEK TUTAR", "GENEL TOPLAM", "VERGILER DAHIL", "TOPLAM TUTAR"];

  for (const label of netLabels) {
    const row = normalizedLines.find((item) => item.normalized.includes(label));
    const value = row ? getLastNumberFromLine(row.raw) : null;
    if (value != null) return value;
  }

  for (const label of grossLabels) {
    const row = normalizedLines.find((item) => item.normalized.includes(label));
    const value = row ? getLastNumberFromLine(row.raw) : null;
    if (value != null) {
      const rate = Number(kdvRate);
      return Number.isFinite(rate) && rate > 0 ? Number((value / (1 + rate / 100)).toFixed(2)) : value;
    }
  }

  const allAmounts = lines
    .map(getLastNumberFromLine)
    .filter((value) => value != null && value > 0)
    .sort((a, b) => b - a);
  return allAmounts[0] ?? null;
};

const fixPdfText = (value = "") =>
  String(value)
    .replace(/Ä°/g, "I")
    .replace(/Ä±/g, "i")
    .replace(/Å/g, "S")
    .replace(/ÅŸ/g, "s")
    .replace(/Ä/g, "G")
    .replace(/ÄŸ/g, "g")
    .replace(/Ãœ/g, "U")
    .replace(/Ã¼/g, "u")
    .replace(/Ã–/g, "O")
    .replace(/Ã¶/g, "o")
    .replace(/Ã‡/g, "C")
    .replace(/Ã§/g, "c")
    .replace(/Ý/g, "I")
    .replace(/Ð/g, "G")
    .replace(/Þ/g, "S");

const normalizePdfText = (value = "") =>
  fixPdfText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleUpperCase("tr-TR")
    .replace(/[İI]/g, "I")
    .replace(/[Ş]/g, "S")
    .replace(/[Ğ]/g, "G")
    .replace(/[Ü]/g, "U")
    .replace(/[Ö]/g, "O")
    .replace(/[Ç]/g, "C")
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getInvoiceLines = (text) =>
  fixPdfText(text)
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

const containsAnyLabel = (line, labels) => {
  const normalized = normalizePdfText(line);
  return labels.some((label) => normalized.includes(label));
};

const findValueNearLabels = (lines, labels, valueRegex, options = {}) => {
  const windowSize = options.windowSize ?? 1;
  const rejectLabels = options.rejectLabels || [];

  for (let i = 0; i < lines.length; i++) {
    if (!containsAnyLabel(lines[i], labels)) continue;
    if (rejectLabels.length && containsAnyLabel(lines[i], rejectLabels)) continue;

    for (let j = 0; j <= windowSize && i + j < lines.length; j++) {
      const match = lines[i + j].match(valueRegex);
      if (match?.[1]) return String(match[1]).trim();
      if (match?.[0]) return String(match[0]).trim();
    }
  }

  return "";
};

const getSellerSection = (lines) => {
  const cutLabels = [
    "SAYIN",
    "ALICI",
    "MUSTERI",
    "MUSTERI BILGILERI",
    "ALICI BILGILERI",
    "MAL HIZMET",
    "SIRA NO",
    "URUN",
    "ACIKLAMA",
  ];
  const cutIndex = lines.findIndex((line, index) => index > 2 && containsAnyLabel(line, cutLabels));
  const topLines = cutIndex > 4 ? lines.slice(0, cutIndex) : lines.slice(0, 45);
  return topLines.join("\n");
};

const findFirmBetter = (text, firmalar) => {
  const lines = getInvoiceLines(text);
  const sellerText = getSellerSection(lines);
  const sellerTaxNumbers = extractTaxNumbers(sellerText);
  const allTaxNumbers = extractTaxNumbers(text);

  const taxMatchedSeller = firmalar.find((firma) => {
    const firmaTax = String(firma.vergiNo || firma.vkn || firma.taxNo || "").replace(/\D/g, "");
    return firmaTax && sellerTaxNumbers.has(firmaTax);
  });
  if (taxMatchedSeller) return taxMatchedSeller;

  const sellerNormalized = normalizePdfText(sellerText);
  const allNormalized = normalizePdfText(text);

  const scored = firmalar
    .map((firma) => {
      const normalizedName = stripCompanyWords(firma.adi || "");
      const tokens = normalizedName.split(" ").filter((part) => part.length > 2);
      const sellerHits = tokens.filter((token) => sellerNormalized.includes(token)).length;
      const allHits = tokens.filter((token) => allNormalized.includes(token)).length;
      const tax = String(firma.vergiNo || firma.vkn || firma.taxNo || "").replace(/\D/g, "");
      const taxScore = tax && allTaxNumbers.has(tax) ? 0.25 : 0;
      const sellerScore = tokens.length ? sellerHits / tokens.length : 0;
      const allScore = tokens.length ? allHits / tokens.length : 0;
      return { firma, score: sellerScore * 1.2 + allScore * 0.35 + taxScore };
    })
    .filter(({ score }) => score >= 0.7)
    .sort((a, b) => b.score - a.score || String(b.firma.adi || "").length - String(a.firma.adi || "").length);

  return scored[0]?.firma;
};

const findPlateBetter = (text, plakalar) => {
  const lines = getInvoiceLines(text);
  const plateLabels = ["PLAKA", "ARAC PLAKA", "ARAC", "TASIT", "KAMYON"];
  const labeledText = lines
    .filter((line, index) =>
      containsAnyLabel(line, plateLabels) ||
      (index > 0 && containsAnyLabel(lines[index - 1], plateLabels))
    )
    .join(" ");
  const fullCompact = normalizePdfText(text).replace(/\s/g, "");
  const labeledCompact = normalizePdfText(labeledText).replace(/\s/g, "");

  const knownFromLabel = plakalar.find((p) => {
    const plate = normalizePdfText(p || "").replace(/\s/g, "");
    return plate && labeledCompact.includes(plate);
  });
  if (knownFromLabel) return knownFromLabel;

  const knownAnywhere = plakalar.find((p) => {
    const plate = normalizePdfText(p || "").replace(/\s/g, "");
    return plate && fullCompact.includes(plate);
  });
  if (knownAnywhere) return knownAnywhere;

  const labeledMatch = normalizePdfText(labeledText).match(/\b(\d{2}\s?[A-Z]{1,3}\s?\d{2,4})\b/);
  return labeledMatch?.[1]?.replace(/\s+/g, " ") || "";
};

const findDateBetter = (text, fallbackDate) => {
  const lines = getInvoiceLines(text);
  const dateRaw = findValueNearLabels(
    lines,
    ["FATURA TARIHI", "DUZENLEME TARIHI", "BELGE TARIHI", "TARIH"],
    /(\d{1,2}[./-]\d{1,2}[./-]\d{4})/,
    { windowSize: 1, rejectLabels: ["VADE", "SEVK", "IRSALIYE"] }
  ) || findFirst(text, [/(\d{1,2}[./-]\d{1,2}[./-]\d{4})/]);

  return toInputDate(dateRaw) || fallbackDate;
};

const findKdvBetter = (text, firma) => {
  const lines = getInvoiceLines(text);
  const kdvLines = lines.filter((line) => containsAnyLabel(line, ["KDV", "KATMA DEGER"]));
  const ratePattern = /(?:%[\s:]*(1|8|10|18|20)(?:[,.]0+)?\b|\b(1|8|10|18|20)(?:[,.]0+)?\s*%)/i;

  for (const line of kdvLines) {
    const match = line.match(ratePattern);
    const rate = match?.[1] || match?.[2];
    if (rate) return rate;
  }

  const explicitRate = findValueNearLabels(
    lines,
    ["KDV ORANI", "KDV %", "KDV"],
    /\b(1|8|10|18|20)(?:[,.]0+)?\b/,
    { windowSize: 1 }
  );
  if (explicitRate) return String(parseTRNumber(explicitRate));

  return firma?.kdvOrani ?? "";
};

const findAmountBetter = (text, kdvRate) => {
  const lines = getInvoiceLines(text);
  const normalizedLines = lines.map((line) => ({ raw: line, normalized: normalizePdfText(line) }));
  const netLabels = [
    "MAL HIZMET TOPLAM",
    "MAL HIZMET TOPLAM TUTARI",
    "HESAPLANAN MATRAH",
    "KDV MATRAH",
    "MATRAH",
    "ARA TOPLAM",
    "VERGI HARIC",
  ];
  const grossLabels = ["ODENECEK TUTAR", "GENEL TOPLAM", "VERGILER DAHIL TOPLAM", "TOPLAM TUTAR"];

  for (const label of netLabels) {
    const row = normalizedLines.find((item) => item.normalized.includes(label));
    const value = row ? getLastNumberFromLine(row.raw) : null;
    if (value != null && value > 0) return value;
  }

  for (const label of grossLabels) {
    const row = normalizedLines.find((item) => item.normalized.includes(label));
    const value = row ? getLastNumberFromLine(row.raw) : null;
    if (value != null && value > 0) {
      const rate = Number(kdvRate);
      return Number.isFinite(rate) && rate > 0 ? Number((value / (1 + rate / 100)).toFixed(2)) : value;
    }
  }

  return findAmount(text, kdvRate);
};

const findProductNameBetter = (text) => {
  const lines = getInvoiceLines(text);
  const tableStart = lines.findIndex((line) =>
    containsAnyLabel(line, ["MAL HIZMET", "HIZMET", "URUN", "ACIKLAMA"]) &&
    !containsAnyLabel(line, ["TOPLAM", "TUTAR"])
  );
  const stopLabels = ["MAL HIZMET TOPLAM", "KDV", "TOPLAM", "ODENECEK", "VERGI"];

  if (tableStart >= 0) {
    for (let i = tableStart + 1; i < Math.min(lines.length, tableStart + 12); i++) {
      const line = lines[i];
      if (containsAnyLabel(line, stopLabels)) break;
      const withoutNumbers = line
        .replace(/\b\d{1,3}(?:\.\d{3})*(?:,\d{2,4})?\b/g, " ")
        .replace(/\b\d+\b/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (withoutNumbers.length >= 4) return withoutNumbers.toLocaleUpperCase("tr-TR");
    }
  }

  return "";
};

const extractInvoiceFields = (text, firmalar, plakalar, fallbackDate) => {
  const firma = findFirmBetter(text, firmalar);
  const plaka = findPlateBetter(text, plakalar);

  const faturaNo = findFirst(text, [
    /(?:FATURA\s*(?:NO|NUMARASI|NO\.?)|FATURA\s*NO)\s*[:-]?\s*([A-Z0-9-]{4,})/i,
    /(?:SENARYO|ETTN)[\s\S]{0,120}?([A-Z]{1,4}\d{6,}[A-Z0-9-]*)/i,
    /\b([A-Z]{1,4}\d{8,16})\b/i,
  ]);

  const tarihRaw = findFirst(text, [
    /(?:FATURA\s*TARİHİ|FATURA\s*TARIHI|DÜZENLEME\s*TARİHİ|DUZENLEME\s*TARIHI|TARİH|TARIH)\s*[:-]?\s*(\d{1,2}[./-]\d{1,2}[./-]\d{4})/i,
    /(\d{1,2}[./-]\d{1,2}[./-]\d{4})/,
  ]);

  const kdvRaw = findFirst(text, [
    /KDV\s*(?:ORANI|%)?\s*[:-]?\s*%?\s*(\d{1,2}(?:[,.]\d+)?)/i,
    /%(\d{1,2}(?:[,.]\d+)?)\s*KDV/i,
  ]);

  const amountRaw = findFirst(text, [
    /(?:MAL\s*HİZMET\s*TOPLAM|MAL\s*HIZMET\s*TOPLAM|ARA\s*TOPLAM|VERGİ\s*HARİÇ|VERGI\s*HARIC|MATRAH)\s*[:-]?\s*([\d.,]+)/i,
    /(?:ÖDENECEK\s*TUTAR|ODENECEK\s*TUTAR|GENEL\s*TOPLAM|TOPLAM\s*TUTAR)\s*[:-]?\s*([\d.,]+)/i,
  ]);

  const parsedKdv = parseTRNumber(kdvRaw);
  const parsedAmount = parseTRNumber(amountRaw);
  const finalKdv = findKdvBetter(text, firma) || (parsedKdv != null ? parsedKdv : "");
  const finalAmount = findAmountBetter(text, finalKdv) ?? parsedAmount;

  return {
    faturaNo,
    firmaAdi: firma?.adi || "",
    firmaId: firma?.id || "",
    tarih: findDateBetter(text, fallbackDate) || toInputDate(tarihRaw) || fallbackDate,
    plaka: plaka || "",
    kdv: finalKdv !== "" && finalKdv != null ? String(finalKdv) : "",
    fiyat: finalAmount != null ? String(finalAmount) : "",
    urunAdi: findProductNameBetter(text),
  };
};

const FaturaGirisi = () => {
  const [firmalar, setFirmalar] = useState([]);
  const [plakalar, setPlakalar] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [faturaNolari, setFaturaNolari] = useState([]);
  const [pdfStatus, setPdfStatus] = useState("");
  const [pdfPreview, setPdfPreview] = useState("");
  const [satirlar, setSatirlar] = useState([emptySatir(getBugun())]);

  function getBugun() {
    return new Date().toISOString().split("T")[0];
  }

  const handlePdfOku = async (file) => {
    if (!file) return;
    setPdfPreview("");
    if (file.type !== "application/pdf") {
      alert("Lütfen PDF dosyası seçin.");
      return;
    }

    try {
      setPdfStatus("PDF okunuyor...");
      const buffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buffer, disableWorker: true }).promise;
      const pageTexts = [];

      for (let pageNo = 1; pageNo <= pdf.numPages; pageNo++) {
        const page = await pdf.getPage(pageNo);
        pageTexts.push(await getPdfPageText(page));
      }

      const text = pageTexts.join("\n");
      setPdfPreview(text.slice(0, 4000));
      const extracted = extractInvoiceFields(text, firmalar, plakalar, getBugun());
      const hasAny = Object.values(extracted).some(Boolean);

      if (!hasAny) {
        setPdfStatus("PDF metni okundu fakat eşleşen alan bulunamadı. Tarama/görsel PDF ise OCR gerekir.");
        return;
      }

      const first = {
        ...emptySatir(getBugun()),
        ...extracted,
        urunAdi: extracted.urunAdi || (extracted.fiyat ? "PDF FATURA" : ""),
        adet: 1,
        aciklama: "PDF'den okundu; kaydetmeden önce kontrol edin.",
      };
      setSatirlar([first]);
      setPdfStatus("PDF okundu. Formdaki alanları kontrol edip eksikleri tamamlayın.");
    } catch (error) {
      console.error("PDF okuma hatası:", error);
      setPdfStatus("PDF okunamadı. Dosya metin içermiyor olabilir; bu sürüm OCR yapmaz.");
    }
  };

  // --- veri çekme ---
  useEffect(() => {
    const fetchData = async () => {
      const firmaSnap = await getDocs(collection(db, "firms"));
      const firmaList = firmaSnap.docs
        .filter((d) => d.data().active !== false)
        .map((d) => ({
          id: d.id,
          adi: d.data().firmaAdi,
          kdvOrani: d.data().kdvOrani ?? 18,
          vergiNo: d.data().vergiNo || d.data().vkn || d.data().taxNo || "",
        }))
        .sort((a, b) => a.adi.localeCompare(b.adi));
      setFirmalar(firmaList);

const plakaSnap = await getDocs(collection(db, "plates"));
const plakaList = plakaSnap.docs
  .filter((d) => d.data().active === true) // 👈 Sadece pasif (false) olmayanları al
  .map((d) => d.data().plate)
  .filter((p) => p) // Boş olanları temizle
  .sort();
setPlakalar(plakaList);

      const faturaSnap = await getDocs(collection(db, "faturalar"));
      const eskiUrunler = faturaSnap.docs.flatMap((doc) =>
        (doc.data().satirlar || []).map((s) => s.urunAdi)
      );
      setUrunler([...new Set(eskiUrunler)].sort());

      const noList = faturaSnap.docs
        .filter((d) => !d.data().deleted)
        .map((d) => d.data().faturaNo);
      setFaturaNolari(noList);
    };
    fetchData();
  }, []);

  // --- tablo içi değişiklik ---
  const handleChange = (i, key, value) => {
    const yeni = [...satirlar];
    if (key === "urunAdi") {
      value = value.toLocaleUpperCase("tr-TR");
    }

    // Ondalık sayı kontrolü
    if ((key === "fiyat" || key === "adet") && value) {
      value = value.replace(",", "."); // Virgül yerine nokta
      if (!/^\d*\.?\d*$/.test(value)) return; // Geçersiz giriş engelle
    }

    yeni[i][key] = value;

    if (key === "firmaAdi") {
      const secilen = firmalar.find((f) => f.adi === value);
      if (secilen) {
        yeni[i].firmaId = secilen.id;
        yeni[i].kdv = secilen.kdvOrani;
      } else {
        yeni[i].firmaId = "";
        yeni[i].kdv = "";
      }
      if (i > 0 && !value) yeni[i].firmaAdi = satirlar[i - 1].firmaAdi;
    }

    if (key === "tarih" && i > 0 && !value) yeni[i].tarih = satirlar[i - 1].tarih;
    if (key === "plaka" && i > 0 && !value) yeni[i].plaka = satirlar[i - 1].plaka;

    setSatirlar(yeni);

    // otomatik yeni satır
    if (i === satirlar.length - 1) {
      const { firmaAdi, faturaNo, tarih, urunAdi, fiyat, adet, kdv, firmaId, plaka } = yeni[i];
      if (firmaAdi && faturaNo && tarih && urunAdi && fiyat && adet && kdv !== "" && firmaId && plaka) {
        setSatirlar((prev) => [
          ...prev,
          {
            faturaNo,
            firmaAdi,
            firmaId,
            tarih,
            plaka,
            urunAdi: "",
            fiyat: "",
            adet: 1,
            iskonto: "",
            kdv: yeni[i].kdv,
            aciklama: "",
          },
        ]);
      }
    }
  };

  // --- kaydet ---
  const handleKaydet = async () => {
    const aktifSatirlar = satirlar.filter(
      (s) =>
        s.firmaAdi &&
        s.faturaNo &&
        s.tarih &&
        s.plaka &&
        s.urunAdi &&
        s.fiyat &&
        s.adet &&
        s.kdv !== "" &&
        s.firmaId
    );

    if (aktifSatirlar.length === 0) {
      alert("Kaydedilecek geçerli satır bulunamadı.");
      return;
    }

    const faturaGruplari = aktifSatirlar.reduce((acc, s) => {
      if (!acc[s.faturaNo]) acc[s.faturaNo] = [];
      acc[s.faturaNo].push(s);
      return acc;
    }, {});

    let basariylaKaydedilen = 0;

    for (const [faturaNo, grup] of Object.entries(faturaGruplari)) {
      const ilk = grup[0];

      // aynı fatura kontrolü
      const q = query(
        collection(db, "faturalar"),
        where("faturaNo", "==", faturaNo),
        where("firmaId", "==", ilk.firmaId),
        where("deleted", "==", false)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        alert(`Fatura no zaten kayıtlı: ${faturaNo}`);
        continue;
      }

      const farkliFirma = grup.some((s) => s.firmaAdi !== ilk.firmaAdi);
      const farkliTarih = grup.some((s) => s.tarih !== ilk.tarih);
      if (farkliFirma || farkliTarih) {
        alert(`Fatura No: ${faturaNo} için firma ve tarih aynı olmalıdır.`);
        continue;
      }

      const satirlarVeri = grup.map(({ urunAdi, fiyat, adet, iskonto, kdv, aciklama, plaka }) => ({
        urunAdi,
        fiyat: parseFloat(fiyat),
        adet: parseFloat(adet),
        iskonto: iskonto ? parseFloat(iskonto) : 0,
        kdv: parseFloat(kdv),
        aciklama,
        plaka,
      }));

      await addDoc(collection(db, "faturalar"), {
        faturaNo,
        firmaAdi: ilk.firmaAdi,
        firmaId: ilk.firmaId,
        tarih: Timestamp.fromDate(new Date(ilk.tarih)),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "anonim",
        satirlar: satirlarVeri,
        deleted: false,
        faturaAcik: "",
      });

      basariylaKaydedilen++;
    }

    if (basariylaKaydedilen > 0) {
      alert("Tüm geçerli faturalar kaydedildi ✅");
      setSatirlar([emptySatir(getBugun())]);
      setPdfStatus("");
      setPdfPreview("");
    }
  };

  // --- Toplam Hesaplama ---
const toplam = satirlar.reduce(
  (acc, s) => {
    const adet = parseFloat(s.adet) || 0;
    const fiyat = parseFloat(s.fiyat) || 0;
    const iskonto = parseFloat(s.iskonto) || 0;
    const kdvOrani = parseFloat(s.kdv) || 0;

    const tutar = adet * fiyat;
    const iskontoTutar = tutar * (iskonto / 100);
    const net = tutar - iskontoTutar;
    const kdvTutar = net * (kdvOrani / 100);

    acc.tutar += tutar;
    acc.iskonto += iskontoTutar;
    acc.net += net;
    acc.kdvDahil += net + kdvTutar;
    return acc;
  },
  { tutar: 0, iskonto: 0, net: 0, kdvDahil: 0 }
);



  const format = (num) => new Intl.NumberFormat("tr-TR").format(num.toFixed(2));

  return (
    <div style={{ padding: 20, overflowX: "auto" }}>
      <h2 style={{ textAlign: "left", color: "#334155", fontSize: 22, fontWeight: "bold", marginBottom: 20 }}>
        Fatura Girişi
      </h2>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
          PDF'den doldur:
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => handlePdfOku(e.target.files?.[0])}
            style={{ marginLeft: 8 }}
          />
        </label>
        {pdfStatus && (
          <span style={{ fontSize: 12, color: "#475569" }}>
            {pdfStatus}
          </span>
        )}
      </div>

      {pdfPreview && (
        <details style={{ marginBottom: 16, fontSize: 12, color: "#334155" }}>
          <summary style={{ cursor: "pointer", fontWeight: 700 }}>
            PDF'den okunan metni göster
          </summary>
          <pre style={{
            maxHeight: 220,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            background: "#f8fafc",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            padding: 10,
            marginTop: 8,
          }}>
            {pdfPreview}
          </pre>
        </details>
      )}

      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "1200px", fontSize: "13px" }}>
        <thead style={{ backgroundColor: "#e2e8f0" }}>
          <tr>
            {"Fatura No,Firma,Tarih,Plaka,Ürün Adı,Adet,Fiyat,KDV,İskonto %,Açıklama"
              .split(",")
              .map((h, i) => (
                <th
                  key={i}
                  style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}
                >
                  {h}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s, i) => (
            <tr key={i}>
              {["faturaNo", "firmaAdi", "tarih", "plaka", "urunAdi", "adet", "fiyat", "kdv", "iskonto", "aciklama"].map(
                (key) => (
                  <td key={key} style={{ border: "1px solid #ccc", padding: "4px" }}>
                    <input
                      type={
                        key === "tarih"
                          ? "date"
                          : key === "adet" || key === "fiyat" || key === "kdv"
                          ? "text"
                          : "text"
                      }
                      value={s[key]}
                      onChange={(e) => handleChange(i, key, e.target.value)}
                      style={{ width: "100%", border: "none", outline: "none", fontSize: "12px" }}
                      list={
                        key === "firmaAdi"
                          ? "firmalar"
                          : key === "plaka"
                          ? "plakalar"
                          : key === "urunAdi"
                          ? "urunler"
                          : undefined
                      }
                    />
                  </td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <datalist id="firmalar">{firmalar.map((f, i) => <option key={i} value={f.adi} />)}</datalist>
      <datalist id="plakalar">{plakalar.map((p, i) => <option key={i} value={p} />)}</datalist>
      <datalist id="urunler">{urunler.map((u, i) => <option key={i} value={u} />)}</datalist>

<div style={{ marginTop: 10, fontSize: "13px", fontWeight: "bold" }}>
Toplam Tutar: {format(toplam.tutar)} ₺ | 
İskonto: {format(toplam.iskonto)} ₺ | 
Net Tutar: {format(toplam.net)} ₺ | 
KDV Dahil Toplam: {format(toplam.kdvDahil)} ₺
</div>


      <div style={{ marginTop: 20 }}>
        <button
          onClick={handleKaydet}
          style={{
            backgroundColor: "#4f46e5",
            color: "white",
            padding: "10px 20px",
            fontWeight: "bold",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
          }}
        >
          Kaydet
        </button>
      </div>
    </div>
  );
};

export default FaturaGirisi;
